<script setup lang="ts">
import { ref, Ref } from 'vue';
import { useClickOutside } from '@/composables/clickOutside';
import ColorSelector from '../ColorSelector.vue';
import CInput from '@/components/shared/CInput.vue';
import CButton from '@/components/shared/CButton';
import { ColorPropertyType } from './types';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';

interface Props {
    label: string;
    configKey: ColorPropertyType;
    color?: ColorObject;
    disabled?: boolean;
    highlightStrokeEnabled?: boolean;
    highlightStrokeWidth?: number;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    highlightStrokeEnabled: false,
    highlightStrokeWidth: 0
});

const isHighlightStroke = props.configKey === ColorPropertyType.HighlightStrokeColor;

const emit = defineEmits<{
    (e: 'color-selected', color: FontColorOption): void;
    (e: 'toggle-highlight-stroke'): void;
    (e: 'update-stroke-width', value: number): void;
}>();

const pickerRef: Ref<HTMLElement | undefined> = ref();

const { clickOutsideActive } = useClickOutside(
    pickerRef,
    () => {
        clickOutsideActive.value = false;
    },
    false
);

const toggleColorPicker = () => {
    clickOutsideActive.value = !clickOutsideActive.value;
};

const onColorSelected = (color: FontColorOption) => {
    emit('color-selected', color);
    toggleColorPicker();
};

const onStrokeWidthUpdate = (value: number | string) => {
    emit('update-stroke-width', typeof value === 'number' ? value : parseFloat(value) || 0);
};

const colorHex = () => props.color?.hex || '';
</script>

<template>
    <section class="color-property-item">
        <h6>{{ label }}</h6>
        <div class="border-bottom" />

        <!-- Special handling for Highlight Stroke -->
        <template v-if="isHighlightStroke">
            <template v-if="highlightStrokeEnabled">
                <div v-if="color" class="color-row border-gray-light rounded" @click="toggleColorPicker">
                    <span class="color-preview" :style="{ backgroundColor: colorHex() }" />
                    {{ colorHex() }}
                </div>
                <color-selector
                    ref="pickerRef"
                    :open="clickOutsideActive"
                    :color="color"
                    @cancel="toggleColorPicker"
                    @color-selected="onColorSelected"
                />
                <c-input
                    :value="highlightStrokeWidth"
                    type="number"
                    class="flex-fill"
                    label="Highlight Outline Width"
                    :disabled="disabled"
                    @input="onStrokeWidthUpdate"
                />
            </template>
            <c-button
                class="w-100"
                variant="secondary"
                :text="highlightStrokeEnabled ? 'Remove Highlight Outline' : 'Add Highlight Outline'"
                :icon="highlightStrokeEnabled ? ['far', 'trash'] : ['far', 'plus']"
                :disabled="disabled"
                @click="emit('toggle-highlight-stroke')"
            />
        </template>

        <!-- Regular color picker -->
        <template v-else>
            <div v-if="color" class="color-row border-gray-light rounded" @click="toggleColorPicker">
                <span class="color-preview" :style="{ backgroundColor: colorHex() }" />
                {{ colorHex() }}
            </div>
            <color-selector
                ref="pickerRef"
                :open="clickOutsideActive"
                :color="color"
                @cancel="toggleColorPicker"
                @color-selected="onColorSelected"
            />
        </template>
    </section>
</template>

<style scoped lang="scss">
.color-property-item {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    h6 {
        font-size: 0.875rem;
        margin-bottom: 0;
    }

    &:is(:first-child) {
        margin-bottom: 0.625rem;
    }

    .color-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem;
        cursor: pointer;

        .color-preview {
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 0.125rem;
        }
    }
}
</style>
